import {getAgentsPath} from '@github-ui/agent-sessions/utils/constants'
import type {ClientTool, ClientToolCall} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {ToolCallResult, ToolResultTextContent} from '@microsoft/agent-host-protocol'

import {currentOrigin, sessionUrl, toAbsoluteUrl} from './session-url'

export const CREATE_SESSION_TOOL_NAME = 'create_session'

/** Params for creating a cloud-agent task (subset of the agent-sessions createTask helper). */
export interface CreateTaskParams {
  nwo: string
  problemStatement: string
  baseRef?: string
  createPullRequest?: boolean
  eventType?: string
  eventContent?: string
  eventIdentifiers?: string[]
}

/** Minimal shape of the created-task response (both legacy `{task:{...}}` and REST `{id,...}`). */
interface CreatedTaskResponse {
  task?: {id?: string}
  id?: string
}

/** What the user is asked to approve before a write-capable session is started. */
export interface CreateSessionConfirmation {
  repo: string
  prompt: string
  baseBranch?: string
  createPullRequest: boolean
}

export interface CreateSessionClientToolDeps {
  /** Create a cloud-agent task via Mission Control (browser side, user's credentials). */
  createTask: (params: CreateTaskParams) => Promise<Response>
  /**
   * Ask the user to approve starting the session. Client tools are auto-approved
   * by the relay runtime with no permission UI, so this write-capable tool gates
   * itself on an explicit confirmation before creating anything.
   */
  confirm: (details: CreateSessionConfirmation) => Promise<boolean>
  /** Page origin used to render a full, openable session URL (defaults to the browser origin). */
  origin?: string
}

interface CreateSessionInput {
  repo: string
  prompt: string
  baseBranch?: string
  createPullRequest?: boolean
}

const CREATE_SESSION_TOOL_DEFINITION: ClientTool['definition'] = {
  name: CREATE_SESSION_TOOL_NAME,
  title: 'Start a cloud session',
  description:
    'Hand off work to a new dedicated Copilot cloud agent session on a GitHub repository. Use when the ' +
    'user wants substantive code changes made in the background. The session runs independently and does ' +
    'NOT report back into this chat, so tell the user it is working on its own and they can follow it ' +
    'separately. Requires the target repository.',
  inputSchema: {
    type: 'object',
    properties: {
      repo: {
        type: 'string',
        description: 'Target repository as "owner/name".',
      },
      prompt: {
        type: 'string',
        description: 'The task for the new session: what the cloud agent should do.',
      },
      base_branch: {
        type: 'string',
        description: 'Optional base branch to work from. Omit to use the repository default.',
      },
      create_pull_request: {
        type: 'boolean',
        description: 'Whether the session should open a pull request. Defaults to false.',
      },
    },
    required: ['repo', 'prompt'],
  },
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
}

/**
 * `ClientTool` implementation of `create_session`. Spawns a Copilot cloud agent
 * task on the given repo via Mission Control (browser side, with the user's
 * write-capable credentials). Fire and forget: the session runs independently
 * and does not report back into this chat.
 */
export function buildCreateSessionClientTool(deps: CreateSessionClientToolDeps): ClientTool {
  return {
    definition: CREATE_SESSION_TOOL_DEFINITION,
    fulfil: call => fulfilCreateSession(call, deps),
  }
}

async function fulfilCreateSession(call: ClientToolCall, deps: CreateSessionClientToolDeps): Promise<ToolCallResult> {
  let input: CreateSessionInput
  try {
    input = parseCreateSessionInput(call.toolInput)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    return {
      success: false,
      pastTenseMessage: 'Failed to start a session',
      error: {message},
      content: [sessionFailureContent(message, deps.origin)],
    }
  }

  let submissionStarted = false
  try {
    const createPullRequest = input.createPullRequest ?? false
    const approved = await deps.confirm({
      repo: input.repo,
      prompt: input.prompt,
      ...(input.baseBranch ? {baseBranch: input.baseBranch} : {}),
      createPullRequest,
    })
    if (!approved) {
      return {
        success: true,
        pastTenseMessage: 'Did not start a session',
        content: [
          textToolContent(
            'The user declined to start the cloud agent session, so nothing was created. Do not retry ' +
              'unless they ask again.',
          ),
        ],
      }
    }

    const createTaskParams = {
      nwo: input.repo,
      problemStatement: input.prompt,
      baseRef: input.baseBranch,
      createPullRequest,
      eventType: 'copilot_quick_chat',
      eventContent: input.prompt,
      eventIdentifiers: [crypto.randomUUID()],
    }
    submissionStarted = true
    const response = await deps.createTask(createTaskParams)
    if (!response.ok) {
      throw new Error(`Failed to start a session (HTTP ${response.status}).`)
    }
    const data = (await response.json()) as CreatedTaskResponse
    const sessionId = data.task?.id ?? data.id
    const url = sessionId ? sessionUrl(sessionId, deps.origin ?? currentOrigin(), input.repo) : ''
    return {
      success: true,
      pastTenseMessage: 'Started a cloud agent session',
      structuredContent: {
        repo: input.repo,
        ...(sessionId ? {session_id: sessionId} : {}),
        ...(url ? {url} : {}),
      },
      content: [
        textToolContent(
          `Started a cloud agent session on ${input.repo}${url ? `. Open it at ${url}` : ''}. It runs ` +
            'independently and will not report back into this chat, so share the link with the user so they ' +
            'can follow it, and tell them it is working on its own.',
        ),
      ],
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    return {
      success: false,
      pastTenseMessage: 'Failed to start a session',
      error: {message},
      content: [
        submissionStarted
          ? sessionSubmissionUncertaintyContent(deps.origin)
          : sessionFailureContent(message, deps.origin),
      ],
    }
  }
}

export function parseCreateSessionInput(toolInput: string | undefined): CreateSessionInput {
  if (!toolInput) throw new Error(`${CREATE_SESSION_TOOL_NAME} requires repo and prompt.`)

  let parsed: unknown
  try {
    parsed = JSON.parse(toolInput)
  } catch {
    throw new Error(`${CREATE_SESSION_TOOL_NAME} requires JSON input.`)
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error(`${CREATE_SESSION_TOOL_NAME} requires repo and prompt.`)
  }

  const {
    repo,
    prompt,
    base_branch: baseBranch,
    create_pull_request: createPullRequest,
  } = parsed as Record<string, unknown>

  if (typeof repo !== 'string' || !isOwnerRepo(repo.trim())) {
    throw new Error(`${CREATE_SESSION_TOOL_NAME} requires repo as "owner/name".`)
  }
  if (typeof prompt !== 'string' || !prompt.trim()) {
    throw new Error(`${CREATE_SESSION_TOOL_NAME} requires a non-empty prompt.`)
  }

  const result: CreateSessionInput = {repo: repo.trim(), prompt: prompt.trim()}
  if (baseBranch !== undefined && baseBranch !== null) {
    if (typeof baseBranch !== 'string' || !baseBranch.trim()) {
      throw new Error(`${CREATE_SESSION_TOOL_NAME} base_branch must be a non-empty string.`)
    }
    result.baseBranch = baseBranch.trim()
  }
  if (createPullRequest !== undefined && createPullRequest !== null) {
    if (typeof createPullRequest !== 'boolean') {
      throw new Error(`${CREATE_SESSION_TOOL_NAME} create_pull_request must be a boolean.`)
    }
    result.createPullRequest = createPullRequest
  }
  return result
}

// A `owner/name` slug: owner is alphanumeric/hyphen (not hyphen-bounded), name is
// alphanumeric/hyphen/underscore/dot. Rejects URL delimiters (and the `.`/`..`
// path segments) so the value cannot alter the request path when interpolated
// into `repoTasksApiPath`.
const OWNER_REPO_PATTERN = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\/[a-zA-Z0-9._-]+$/

function isOwnerRepo(value: string): boolean {
  if (!OWNER_REPO_PATTERN.test(value)) return false
  const name = value.slice(value.indexOf('/') + 1)
  return name !== '.' && name !== '..'
}

function textToolContent(text: string): ToolResultTextContent {
  return {type: 'text', text} as ToolResultTextContent
}

function sessionFailureContent(message: string, origin = currentOrigin()): ToolResultTextContent {
  const agentsUrl = toAbsoluteUrl(getAgentsPath(), origin)
  return textToolContent(
    `${message} No session was created. Do not invent a task URL. If the user wants to view existing cloud agent ` +
      `sessions, direct them to ${agentsUrl}.`,
  )
}

function sessionSubmissionUncertaintyContent(origin = currentOrigin()): ToolResultTextContent {
  const agentsUrl = toAbsoluteUrl(getAgentsPath(), origin)
  return textToolContent(
    `GitHub may have created the session, but this tool did not receive confirmation. Do not invent a task URL or ` +
      `retry automatically. Tell the user to check ${agentsUrl} before trying again.`,
  )
}
